import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth.jsx";
import { Trophy, Calendar, MapPin, BookOpen, Star, PlayCircle } from "lucide-react";
import Walkthrough from "../components/Walkthrough.jsx";
import { openPlayStoreRating } from "../lib/appFeedback";

export default function Profile() {
  const { user, access } = useAuth();
  const [progress, setProgress] = useState(null);
  const [showTour, setShowTour] = useState(false);

  useEffect(() => {
    api.get("/profile/progress").then((r) => setProgress(r.data)).catch(() => {});
  }, []);

  if (!user) return null;
  const totalCh = progress?.total_chapters || 0;
  const chDone = progress?.chapters?.filter((c) => c.mcqs_done).length || 0;
  const bestMock = progress?.mocks?.reduce((b, m) => (m.score > (b?.score ?? -1) ? m : b), null);

  return (
    <div className="space-y-6">
      {showTour && <Walkthrough forceOpen onClose={() => setShowTour(false)} />}
      <div className="brimi-card p-6 md:p-8 flex items-center gap-5">
        <div className="w-16 h-16 rounded-2xl bg-brimi-navy text-white grid place-items-center font-heading font-bold text-2xl" data-testid="profile-avatar">
          {user.name?.[0]?.toUpperCase() || "B"}
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-heading font-semibold text-brimi-navyText">{user.name}</h1>
            {user.is_owner && (
              <span
                className="brimi-pill bg-brimi-navy text-white"
                title="Owner account"
                data-testid="owner-badge-profile"
              >
                ★ Owner
              </span>
            )}
          </div>
          <p className="text-brimi-navyText/65 text-sm">{user.email}</p>
        </div>
        <div className="text-right">
          <p className="text-xs uppercase tracking-widest font-semibold text-brimi-navyText/60">Plan</p>
          <p className="font-heading font-semibold text-brimi-navyText" data-testid="profile-plan">
            {access?.status === "paid" ? "Subscribed" : access?.status === "trial" ? `Trial · ${access?.days_left}d left` : "Expired"}
          </p>
          {access?.status !== "paid" && (
            <Link to="/paywall" className="text-brimi-terracotta text-sm font-semibold hover:underline" data-testid="profile-upgrade-link">
              Upgrade
            </Link>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-5">
        <Stat icon={BookOpen} label="Chapters covered" value={`${chDone}/${totalCh}`} color="navy" testid="stat-chapters" />
        <Stat icon={Trophy} label="Best mock score" value={bestMock ? `${bestMock.score}/${bestMock.total}` : "—"} color="terracotta" testid="stat-mock" />
        <Stat icon={MapPin} label="Map items placed" value={progress?.map_completed?.length || 0} color="sage" testid="stat-map" />
      </div>

      <div className="brimi-card p-6">
        <h3 className="font-heading font-semibold text-lg text-brimi-navyText mb-4 flex items-center gap-2">
          <Calendar size={18} /> Recent mock tests
        </h3>
        {!progress?.mocks?.length ? (
          <p className="text-brimi-navyText/60 text-sm">No mock tests yet — take your first one.</p>
        ) : (
          <ul className="space-y-2">
            {progress.mocks.slice(0, 8).map((m) => (
              <li key={m.id} className="flex items-center justify-between border-b border-brimi-creamSoft last:border-0 py-2">
                <span className="text-sm text-brimi-navyText/75">
                  {new Date(m.created_at).toLocaleString()}
                </span>
                <span className="font-semibold text-brimi-navyText">
                  {m.score}/{m.total}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="brimi-card p-6">
        <h3 className="font-heading font-semibold text-lg text-brimi-navyText mb-2">Help &amp; feedback</h3>
        <p className="text-sm text-brimi-navyText/70 mb-4">
          Loving BrimiH&amp;G? Please rate us on the Play Store — it takes 20 seconds and helps other
          ICSE students find the app.
        </p>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={openPlayStoreRating}
            className="brimi-btn-primary"
            data-testid="profile-rate-btn"
          >
            <Star size={16} /> Rate BrimiH&amp;G
          </button>
          <button
            onClick={() => setShowTour(true)}
            className="inline-flex items-center gap-2 rounded-xl border border-brimi-creamSoft bg-white px-4 py-2.5 font-semibold text-brimi-navyText hover:bg-brimi-cream/50 transition-colors"
            data-testid="profile-replay-tour-btn"
          >
            <PlayCircle size={16} /> Replay walkthrough
          </button>
        </div>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, color, testid }) {
  const colorMap = {
    navy: "bg-brimi-navy/10 text-brimi-navy",
    terracotta: "bg-brimi-terracotta/15 text-brimi-terracotta",
    sage: "bg-brimi-sage/15 text-brimi-sage",
  };
  return (
    <div className="brimi-card p-5" data-testid={testid}>
      <div className={`w-10 h-10 grid place-items-center rounded-xl ${colorMap[color]}`}>
        <Icon size={18} />
      </div>
      <p className="text-xs uppercase tracking-widest font-semibold text-brimi-navyText/55 mt-3">{label}</p>
      <p className="text-2xl font-heading font-bold text-brimi-navyText mt-1">{value}</p>
    </div>
  );
}
